import React, { useState, useEffect } from "react";
import { Box, Typography, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { EmailInput, OTPInput } from "../UIElements/inputs";
import { Link, useNavigate } from "react-router-dom";
import { PATH_AUTH, PATH_DASHBOARD } from "../../routes/path";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { SignupPropType } from "../../interface/auth";

import {
  signUpSchemaOne,
  signUpSchemaTwo,
  signUpSchemaThree,
} from "../../validate/authentication";
import { ValidTypes } from "../../enum/authentication";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CustomInput, CustomSelect } from "../UIElements/input-verification";
import InputPassword from "../UIElements/input-password";
import { useMutation } from "@tanstack/react-query";
import {
  apiSignUpFacility,
  apiVerifyAccount,
  apiVerifyEmail,
} from "../../api/auth";
import { useNotificationService } from "../notification";

export const SignupDetailsOne = ({
  pageHandler,
  changeValues,
}: SignupPropType) => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xs");
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotificationService();

  // set up validation
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(signUpSchemaOne),
  });

  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiVerifyEmail,
    onSuccess: (data) => {
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        changeValues((prevState) => ({
          ...prevState,
          email: data?.data?.data?.email ?? "",
          token: data?.data?.data?.token ?? "",
        }));
        // next page
        pageHandler();
      }
      if ("msg" in data) {
        showNotification(data?.msg ?? "", "warning");
      }
    },
    onError: (error) => {
      console.error("Email verification failed", error);
      // Handle error
      showNotification("Email verification failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    const { email } = value;
    mutation.mutate({ email });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <EmailInput
          errors={errors}
          labelText="Email"
          placeholder="Enter email"
          register={register}
        />
        <Box></Box>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
          sx={{ my: "1.5rem" }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lexend",
              fontSize: "15px",
              fontWeight: 400,
              lineHeight: "20px",
              letterSpacing: "-0.23999999463558197px",
            }}
          >
            Already have an account?
          </Typography>
          <Link
            style={{
              color: theme.palette.common.black,
              textDecorationLine: "none",
            }}
            to={PATH_AUTH.signIn}
          >
            <Typography
              variant="body2"
              color={"primary"}
              sx={{
                fontFamily: "Lexend",
                fontSize: "15px",
                fontWeight: 700,
                lineHeight: "20px",
                letterSpacing: "-0.23999999463558197px",
              }}
            >
              Login
            </Typography>
          </Link>
        </Stack>
      </Box>
    </Box>
  );
};

export const SignupDetailsTwo = ({
  pageHandler,
  values,
  changeValues,
  prevPage,
}: SignupPropType) => {
  const isMedium = useResponsive("down", "md", "xs");
  const { showNotification } = useNotificationService();
  const theme = useTheme();
  const [countdown, setCountdown] = useState(120); // 120 seconds = 2 minutes
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // set up validation
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(signUpSchemaTwo),
  });

  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiVerifyAccount,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        pageHandler();
      } else {
        showNotification("Verification failed", "error");
      }
    },
    onError: (error) => {
      // Handle error
      showNotification("Verification failed", "error");
    },
  });

  // set up resend email notification
  const resendEmail = useMutation({
    mutationFn: apiVerifyEmail,
    onSuccess: (data) => {
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
        changeValues((prevState) => ({
          ...prevState,
          email: data?.data?.data?.email ?? "",
          token: data?.data?.data?.token ?? "",
        }));
      }
    },
    onError: (error) => {
      console.error("Email verification failed", error);
      // Handle error
      showNotification("Email verification failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    const { code } = value;
    const stringNumber = code;
    const current_code = stringNumber.replace(/-/g, "");

    mutation.mutate({ code: current_code, token: values?.token });
  };

  const handleResend = async () => {
    if (canResend) {
      // Your resend logic here
      resendEmail.mutate({ email: values?.email });
      setCountdown(120); // Reset countdown
      setCanResend(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <OTPInput
          labelText="OTP"
          placeholder="0 - 0 - 0 - 0"
          errors={errors}
          register={register}
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"end"}
          sx={{ my: "1.5rem" }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              marginLeft: "auto",
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Continue
          </LoadingButton>
        </Stack>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography
            onClick={handleResend}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              color: canResend
                ? theme.palette.primary.main
                : theme.palette.text.disabled,
              cursor: canResend ? "pointer" : "default",
              "&:hover": {
                textDecoration: canResend ? "underline" : "none",
              },
            }}
          >
            {canResend ? "Resend OTP" : `Resend OTP (${formatTime(countdown)})`}
          </Typography>
          <Typography
            onClick={prevPage}
            sx={{
              fontSize: "16px",
              lineHeight: "27px",
              cursor: "pointer",
              color: theme.palette.text.disabled,
            }}
          >
            Change email
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export const SignupDetailsThree = ({
  pageHandler,
  values,
  changeValues,
  prevPage,
}: SignupPropType) => {
  const isMedium = useResponsive("down", "md", "xs");
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();
  const theme = useTheme();
  const [user_data, setUser_Data] = useState({
    email: "",
    facility_size: "",
    name: "",
    password: "",
    facility_type: "",
  });
  // set up validation
  const {
    register,
    formState: { errors, isSubmitSuccessful },
    handleSubmit,
  } = useForm({
    resolver: zodResolver(signUpSchemaThree),
  });

  // set up react query mutation
  const mutation = useMutation({
    mutationFn: apiSignUpFacility,
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }

      console.log(data);
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Sign Up Completed", "success");
        navigate(PATH_AUTH.verification, {
          state: { ...user_data, _id: data?.data?.data?._id },
        });
      }
    },
    onError: (error) => {
      console.error("Sign up process failed", error);
      showNotification("Sign up process failed", "error");
    },
  });

  const handleOnSubmit = async (value: any) => {
    const data = {
      email: values?.email,
      facility_size: String(value?.facility_size),
      name: value?.name,
      password: value?.password,
      facility_type: value?.type,
    };

    setUser_Data({ ...data });
    mutation.mutate(data);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <CustomInput
          name="name"
          labelText="Facility Name"
          placeholder="Enter facility name"
          type="text"
          errors={errors}
          register={register}
        />

        <CustomSelect
          name="type"
          labelText="Facility type"
          placeholder="Select facility type"
          type="text"
          errors={errors}
          ValidTypes={ValidTypes}
          register={register}
        />
        <CustomInput
          name="facility_size"
          labelText="Facility size (number of employees)"
          placeholder="Enter facility size here"
          type="number"
          errors={errors}
          register={(name) => register(name)} //
        />

        <InputPassword
          errors={errors}
          placeholder="Enter password"
          labelText="Password"
          register={register}
        />

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"end"}
          sx={{ my: "1.5rem" }}
        >
          <LoadingButton
            loading={mutation.isPending}
            variant="contained"
            type="submit"
            sx={{
              width: "100%",
              borderRadius: "12px",
              fontWeight: 700,
            }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  );
};
